import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import {
  VStack,
  HStack,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  NumberInput,
  NumberInputField,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Switch,
  Spinner,
  Flex,
  Alert,
  AlertIcon,
  Box,
  ButtonGroup,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSave,
  FiX,
  FiChevronUp,
  FiChevronDown,
} from 'react-icons/fi';
import { MdDragIndicator } from 'react-icons/md';
import toast from 'react-hot-toast';
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

// Sortable Row with both drag handle and arrow buttons
const SortableRow = React.memo(({ activity, index, totalCount, onEdit, onDelete, onMoveUp, onMoveDown }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: activity.id });

  const style = useMemo(() => ({
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    transition: transition || 'transform 150ms ease',
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isDragging ? 'rgba(49, 130, 206, 0.2)' : 'transparent',
  }), [transform, transition, isDragging]);

  const subTasksCount = activity.masterSubTasks?.length || 0;

  return (
    <Tr ref={setNodeRef} style={style}>
      {/* Drag Handle */}
      <Td width="40px" p={2}>
        <Tooltip label="Drag to reorder">
          <Box
            {...attributes}
            {...listeners}
            cursor="grab"
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="gray.400"
            _hover={{ color: "blue.400" }}
            _active={{ cursor: "grabbing" }}
          >
            <MdDragIndicator size={20} />
          </Box>
        </Tooltip>
      </Td>

      {/* Up/Down Arrows */}
      <Td width="80px" p={2}>
        <ButtonGroup size="xs" isAttached variant="outline">
          <IconButton
            aria-label="Move up"
            icon={<FiChevronUp />}
            onClick={() => onMoveUp(index)}
            isDisabled={index === 0}
            size="xs"
          />
          <IconButton
            aria-label="Move down"
            icon={<FiChevronDown />}
            onClick={() => onMoveDown(index)}
            isDisabled={index === totalCount - 1}
            size="xs"
          />
        </ButtonGroup>
      </Td>

      <Td>
        <Badge colorScheme="blue" variant="outline">
          {activity.categoryCode}
        </Badge>
      </Td>
      <Td color="white" fontFamily="mono" fontWeight="bold">
        {activity.code}
      </Td>
      <Td color="white">{activity.name}</Td>
      <Td color="white">{activity.defaultUnit}</Td>
      <Td>
        <Badge variant="outline" colorScheme="green">
          {subTasksCount}
        </Badge>
      </Td>
      <Td>
        <Badge colorScheme={activity.isActive ? "green" : "red"}>
          {activity.isActive ? "Active" : "Inactive"}
        </Badge>
      </Td>
      <Td>
        <HStack spacing={2}>
          <IconButton
            aria-label="Edit"
            icon={<FiEdit2 />}
            size="sm"
            variant="outline"
            onClick={() => onEdit(activity)}
          />
          <IconButton
            aria-label="Delete"
            icon={<FiTrash2 />}
            size="sm"
            variant="outline"
            colorScheme="red"
            isDisabled={subTasksCount > 0}
            onClick={() => onDelete(activity)}
          />
        </HStack>
      </Td>
    </Tr>
  );
});

SortableRow.displayName = 'SortableRow';

const MasterActivityManager = ({ categories, onDataChange }) => {
  const [activities, setActivities] = useState([]);
  const [originalActivities, setOriginalActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [formData, setFormData] = useState({
    masterCategoryId: '',
    code: '',
    name: '',
    description: '',
    defaultUnit: 'No',
    order: 0,
    isActive: true
  });
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [hasOrderChanges, setHasOrderChanges] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);

  const {
    isOpen: isFormOpen,
    onOpen: onFormOpen,
    onClose: onFormClose
  } = useDisclosure();

  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose
  } = useDisclosure();

  // Fast drag sensors
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 3,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    })
  );

  useEffect(() => {
    fetchAllActivities();
  }, []);

  const fetchAllActivities = useCallback(async () => {
    setLoading(true);
    try {
      const allActivities = [];
      for (const category of categories) {
        if (category.masterActivities) {
          const sortedCategoryActivities = [...category.masterActivities]
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map(activity => ({
              ...activity,
              categoryName: category.name,
              categoryCode: category.code
            }));
          allActivities.push(...sortedCategoryActivities);
        }
      }
      setActivities(allActivities);
      setOriginalActivities(allActivities);
      setHasOrderChanges(false);
    } catch (error) {
      console.error('Error fetching activities:', error);
      toast.error('Failed to fetch activities');
    } finally {
      setLoading(false);
    }
  }, [categories]);

  // Drag end handler
  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setActivities((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
      setHasOrderChanges(true);
    }
  }, []);

  // Arrow up handler
  const moveUp = useCallback((index) => {
    if (index === 0) return;
    
    setActivities((items) => {
      const newItems = [...items];
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      return newItems;
    });
    setHasOrderChanges(true);
  }, []);

  // Arrow down handler
  const moveDown = useCallback((index) => {
    setActivities((items) => {
      if (index === items.length - 1) return items;
      
      const newItems = [...items];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      return newItems;
    });
    setHasOrderChanges(true);
  }, []);

  const handleSaveOrder = useCallback(async () => {
    setSavingOrder(true);
    try {
      const updates = activities.map((activity, index) => ({
        id: activity.id,
        order: index
      }));

      const { data } = await api.put('/super-admin/master/activities/bulk-order', {
        activities: updates
      });

      if (data.success) {
        toast.success('Order saved');
        setOriginalActivities([...activities]);
        setHasOrderChanges(false);
        onDataChange?.();
      } else {
        toast.error(data.message || 'Failed');
      }
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Failed to save');
    } finally {
      setSavingOrder(false);
    }
  }, [activities, onDataChange]);

  const handleCancelOrder = useCallback(() => {
    setActivities([...originalActivities]);
    setHasOrderChanges(false);
    toast.success('Cancelled');
  }, [originalActivities]);

  const handleCreate = useCallback(() => {
    setSelectedActivity(null);
    setFormData({
      masterCategoryId: categories[0]?.id || '',
      code: '',
      name: '',
      description: '',
      defaultUnit: 'No',
      order: 0,
      isActive: true
    });
    setIsEditing(false);
    onFormOpen();
  }, [categories, onFormOpen]);

  const handleEdit = useCallback((activity) => {
    setSelectedActivity(activity);
    setFormData({
      masterCategoryId: activity.masterCategoryId,
      code: activity.code,
      name: activity.name,
      description: activity.description || '',
      defaultUnit: activity.defaultUnit,
      order: activity.order,
      isActive: activity.isActive
    });
    setIsEditing(true);
    onFormOpen();
  }, [onFormOpen]);

  const handleDeleteClick = useCallback((activity) => {
    setSelectedActivity(activity);
    onDeleteOpen();
  }, [onDeleteOpen]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedActivity) return;

    setSubmitting(true);
    try {
      const { data } = await api.delete(`/super-admin/master/activities/${selectedActivity.id}`);

      if (data.success) {
        toast.success('Deleted');
        await fetchAllActivities();
        onDataChange?.();
        onDeleteClose();
      } else {
        toast.error(data.message || 'Failed');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Failed to delete');
    } finally {
      setSubmitting(false);
    }
  }, [selectedActivity, onDataChange, onDeleteClose, fetchAllActivities]);

  const handleSubmit = useCallback(async () => {
    if (!formData.code || !formData.name || !formData.masterCategoryId) {
      toast.error('Code, name, and category required');
      return;
    }

    setSubmitting(true);
    try {
      const url = isEditing
        ? `/super-admin/master/activities/${selectedActivity.id}`
        : `/super-admin/master/categories/${formData.masterCategoryId}/activities`;

      const method = isEditing ? 'put' : 'post';
      const { data } = await api[method](url, formData);

      if (data.success) {
        toast.success(`${isEditing ? 'Updated' : 'Created'}`);
        await fetchAllActivities();
        onDataChange?.();
        onFormClose();
      } else {
        toast.error(data.message || 'Failed');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed');
    } finally {
      setSubmitting(false);
    }
  }, [formData, isEditing, selectedActivity, onDataChange, onFormClose, fetchAllActivities]);

  const activityIds = useMemo(() => activities.map(a => a.id), [activities]);

  if (loading) {
    return (
      <Flex justify="center" align="center" h="300px">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  return (
    <VStack spacing={4} align="stretch">
      <Card bg="gray.800" borderColor="gray.700">
        <CardHeader>
          <Flex justify="space-between" align="center">
            <VStack align="start" spacing={1}>
              <Heading size="md" color="white">Master Activities</Heading>
              <Text color="gray.400" fontSize="sm">
                Drag rows or use arrows to reorder
              </Text>
            </VStack>
            <HStack spacing={3}>
              {hasOrderChanges && (
                <>
                  <Button leftIcon={<FiX />} size="sm" variant="outline" onClick={handleCancelOrder}>
                    Cancel
                  </Button>
                  <Button leftIcon={<FiSave />} size="sm" colorScheme="green" onClick={handleSaveOrder} isLoading={savingOrder}>
                    Save Order
                  </Button>
                </>
              )}
              <Button leftIcon={<FiPlus />} size="sm" colorScheme="blue" onClick={handleCreate} isDisabled={categories.length === 0}>
                Add
              </Button>
            </HStack>
          </Flex>
        </CardHeader>
      </Card>

      {hasOrderChanges && (
        <Alert status="warning" variant="left-accent">
          <AlertIcon />
          Unsaved changes
        </Alert>
      )}

      <Card bg="gray.800" borderColor="gray.700">
        <CardBody p={0}>
          {categories.length === 0 ? (
            <Box p={8}>
              <Alert status="info">
                <AlertIcon />
                Create categories first
              </Alert>
            </Box>
          ) : activities.length === 0 ? (
            <Box p={8} textAlign="center">
              <Text color="gray.400" mb={4}>No activities</Text>
              <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={handleCreate}>
                Add Activity
              </Button>
            </Box>
          ) : (
            <Box overflowX="auto">
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <Table variant="simple" size="sm">
                  <Thead>
                    <Tr>
                      <Th color="gray.400" width="40px"></Th>
                      <Th color="gray.400" width="80px">Order</Th>
                      <Th color="gray.400">Category</Th>
                      <Th color="gray.400">Code</Th>
                      <Th color="gray.400">Name</Th>
                      <Th color="gray.400">Unit</Th>
                      <Th color="gray.400">Sub-Tasks</Th>
                      <Th color="gray.400">Status</Th>
                      <Th color="gray.400">Actions</Th>
                    </Tr>
                  </Thead>
                  <SortableContext items={activityIds} strategy={verticalListSortingStrategy}>
                    <Tbody>
                      {activities.map((activity, index) => (
                        <SortableRow
                          key={activity.id}
                          activity={activity}
                          index={index}
                          totalCount={activities.length}
                          onEdit={handleEdit}
                          onDelete={handleDeleteClick}
                          onMoveUp={moveUp}
                          onMoveDown={moveDown}
                        />
                      ))}
                    </Tbody>
                  </SortableContext>
                </Table>
              </DndContext>
            </Box>
          )}
        </CardBody>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size="lg">
        <ModalOverlay />
        <ModalContent bg="gray.800" borderColor="gray.700">
          <ModalHeader color="white">{isEditing ? 'Edit' : 'Create'} Activity</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel color="gray.300">Category</FormLabel>
                <Select value={formData.masterCategoryId} onChange={(e) => setFormData(prev => ({ ...prev, masterCategoryId: e.target.value }))} bg="gray.700" color="white" borderColor="gray.600">
                  <option value="">Select</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.code} - {cat.name}</option>
                  ))}
                </Select>
              </FormControl>

              <HStack spacing={4}>
                <FormControl isRequired flex="1">
                  <FormLabel color="gray.300">Code</FormLabel>
                  <Input value={formData.code} onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))} placeholder="HVAC-001" bg="gray.700" color="white" borderColor="gray.600" />
                </FormControl>
                <FormControl width="120px">
                  <FormLabel color="gray.300">Order</FormLabel>
                  <NumberInput value={formData.order} onChange={(value) => setFormData(prev => ({ ...prev, order: parseInt(value) || 0 }))} min={0}>
                    <NumberInputField bg="gray.700" color="white" borderColor="gray.600" />
                  </NumberInput>
                </FormControl>
              </HStack>

              <FormControl isRequired>
                <FormLabel color="gray.300">Name</FormLabel>
                <Input value={formData.name} onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} bg="gray.700" color="white" borderColor="gray.600" />
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300">Unit</FormLabel>
                <Select value={formData.defaultUnit} onChange={(e) => setFormData(prev => ({ ...prev, defaultUnit: e.target.value }))} bg="gray.700" color="white" borderColor="gray.600">
                  <option value="No">No</option>
                  <option value="m">m</option>
                  <option value="Sq.m">Sq.m</option>
                  <option value="Item">Item</option>
                  <option value="Set">Set</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel color="gray.300">Description</FormLabel>
                <Textarea value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} bg="gray.700" color="white" borderColor="gray.600" rows={3} />
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <FormLabel color="gray.300" mb={0}>Active</FormLabel>
                <Switch isChecked={formData.isActive} onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))} colorScheme="green" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={submitting}>
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="gray.800" borderColor="gray.700">
          <ModalHeader color="white">Confirm Delete</ModalHeader>
          <ModalCloseButton color="white" />
          <ModalBody>
            <Text color="gray.300">
              Delete <Text as="span" fontWeight="bold" color="white">{selectedActivity?.code} - {selectedActivity?.name}</Text>?
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDeleteConfirm} isLoading={submitting}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default MasterActivityManager;
